
//#region Imports
import { api } from "_shared/_stores/api";
import SearchResults from "_shared/_api/search/search-results";
import type { MarketplaceForumSearchRequestJSON } from "marketplace/_schema/marketplace-forum-search-request-schema";
import MarketplaceSearchRequestSchema, { MarketplaceSearchRequestJSON } from "marketplace/_schema/marketplace-search-request-schema";
import MarketplaceSearchResponseSchema, { MarketplaceSearchInfoJSON } from "marketplace/_schema/marketplace-search-response-schema";
import {
  marketplaceSearchResponseMiddleware,
  marketplaceSearchResponseWithEnvelopeMiddleware,
} from "marketplace/_api/marketplace-search-response-middleware";
import IndividualSchema, { IndividualJSON } from "marketplace/individuals/_schemas/individual-schema";
import MarketplaceTeamSearch from "marketplace/teams/_api/request-contracts/marketplace-team-search";
import TeamSchema, { TeamJSON } from "marketplace/teams/_schema/team-schema";
import ForumSchema, { ForumJSON } from "messaging/forums/_schemas/forum-schema";
import CompanySchema, { CompanyJSON } from "marketplace/companies/company-schema";
import z from "zod";
//#endregion

//#region API
export const marketplaceApi = api.injectEndpoints({
  endpoints: (build) => ({
    searchAll: build.query<SearchResults<MarketplaceSearchInfoJSON>, { search: MarketplaceSearchRequestJSON }>({
      query: ({ search }) => {
        const params = MarketplaceSearchRequestSchema.toSearchParams(search);
        return {
          url: "/marketplace/all",
          params,
        };
      },
      rawResponseSchema: MarketplaceSearchResponseSchema.ResponseJSONZ,
      onSchemaFailure: (error, info) => {
        console.group(`Schema failure: ${error.schemaName}`);
        console.error("Endpoint:", info.endpoint);
        console.error("Type:", info.type);
        console.error("Arg:", info.arg);
        console.error("Raw value:", error.value);
        console.error("Issues:", error.issues);
        console.groupEnd();
      },      
      transformResponse: marketplaceSearchResponseWithEnvelopeMiddleware,
      providesTags: [{ type: "Marketplace", id: "SearchAll" }],
    }),

    getMarketplaceForums: build.query<ForumJSON[], { search: MarketplaceForumSearchRequestJSON[] }>({
      query: ({ search }) => ({
        url: "/marketplace/forums/search",
        method: "POST",
        body: search,
        headers: { "Content-Type": "application/json-patch+json" },
      }),
      rawResponseSchema: z.array(ForumSchema.ForumJSONZ),
      providesTags: [{ type: "Marketplace", id: "Forums" }],
    }),

    searchIndividuals: build.query<
      SearchResults<MarketplaceSearchInfoJSON>,
      { search: MarketplaceSearchRequestJSON; limitToVendorReps?: boolean }
    >({
      query: ({ search, limitToVendorReps = false }) => {
        const params = MarketplaceSearchRequestSchema.toSearchParams(search);
        params.set("limitToVendorReps", limitToVendorReps.toString());
        return {
          url: "/marketplace/individuals",
          params,
        };
      },
      transformResponse: marketplaceSearchResponseMiddleware,
      providesTags: [{ type: "Marketplace", id: "SearchIndividuals" }],
    }),

    searchTeams: build.query<SearchResults<MarketplaceSearchInfoJSON>, { search: MarketplaceTeamSearch }>({
      query: ({ search }) => {
        const params = search.searchParams;
        return {
          url: "/marketplace/teams",
          params,
        };
      },
      transformResponse: marketplaceSearchResponseMiddleware,
      providesTags: [{ type: "Marketplace", id: "SearchTeams" }],
    }),

    getMarketplaceIndividualById: build.query<IndividualJSON, { individualId: string }>({
      query: ({ individualId }) => ({
        url: `/marketplace/individuals/${individualId}`,
      }),
      rawResponseSchema: IndividualSchema.IndividualJSONZ,
      providesTags: (_result, _error, { individualId }) => [{ type: "Individual", id: individualId }],
    }),

    getMarketplaceTeamById: build.query<TeamJSON, { teamId: string }>({
      query: ({ teamId }) => ({
        url: `/marketplace/teams/${teamId}`,
      }),
      rawResponseSchema: TeamSchema.TeamJSONZ,
      providesTags: (_result, _error, { teamId }) => [{ type: "Marketplace", id: `Team-${teamId}` }],
    }),

    getMarketplaceCompanyById: build.query<CompanyJSON, { companyId: string }>({
      query: ({ companyId }) => ({
        url: `/marketplace/companies/${companyId}`,
      }),
      rawResponseSchema: CompanySchema.CompanyJSONZ,
      providesTags: (_result, _error, { companyId }) => [{ type: "Marketplace", id: `Company-${companyId}` }],
    }),
  }),
});
//#endregion

//#region Exports
export const {
  useSearchAllQuery,
  useGetMarketplaceForumsQuery,
  useSearchIndividualsQuery,
  useSearchTeamsQuery,
  useGetMarketplaceIndividualByIdQuery,
  useGetMarketplaceTeamByIdQuery,
  useGetMarketplaceCompanyByIdQuery,
} = marketplaceApi;
//#endregion
